import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Snackbar,
  Alert,
  InputLabel,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";
import { useAppSettings } from "../../../context/AppSettingsContext";

const CreateCustomerOrderForm = () => {
  const [appUsers, setAppUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const { deliveryConditions, deliveryDate } = useAppSettings();
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [formData, setFormData] = useState({
    app_user_id: "",
    payment_terms: "",
    payment_terms_description: "",
    delivery_title: "",
    delivery_address: "",
    delivery_conditions: "",
    delivery_date: "",
    delivery_date_hours: "",
    delivery_date_days: "",
    status: "pending",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const navigate = useNavigate();

  // Transform delivery conditions string into array of objects
  const deliveryConditionsOptions = React.useMemo(() => {
    if (!deliveryConditions) return [];
    return deliveryConditions
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim(),
      }))
      .filter((condition) => condition.name);
  }, [deliveryConditions]);

  // Transform delivery date string into array of objects
  const deliveryDateOptions = React.useMemo(() => {
    if (!deliveryDate) return [];
    return deliveryDate
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim(),
      }))
      .filter((condition) => condition.name);
  }, [deliveryDate]);

  // Fetch data from APIs
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, productsRes, paymentRes, deliveryRes, brandsRes] = await Promise.all([
          axios.get(API_ENDPOINTS.APP_USER_NAMES),
          axios.get(API_ENDPOINTS.PRODUCT_TYPE_NAMES),
          axios.get(API_ENDPOINTS.PAYMENT_TERMS.BASE),
          axios.get(API_ENDPOINTS.DELIVERY_TERMS.BASE),
          axios.get(API_ENDPOINTS.BRANDS.BASE),
        ]);

        setAppUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.data || []);
        setProducts(
          (Array.isArray(productsRes.data) ? productsRes.data : productsRes.data || []).map(
            (product) => ({
              id: product.id,
              product_name: product.product_name || product.name,
              selected: false,
              brand: "",
              quantity: "",
              pieces: "",
            })
          )
        );
        setPaymentTerms(
          Array.isArray(paymentRes.data.paymentTerms)
            ? paymentRes.data.paymentTerms
            : paymentRes.data || []
        );
        setDeliveryTerms(
          Array.isArray(deliveryRes.data.deliveryTerms)
            ? deliveryRes.data.deliveryTerms
            : deliveryRes.data || []
        );
        setBrands(
          Array.isArray(brandsRes.data) ? brandsRes.data : brandsRes.data.brands || []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setAppUsers([]);
        setProducts([]);
        setPaymentTerms([]);
        setDeliveryTerms([]);
        setBrands([]);
        setSnackbar({
          open: true,
          message: "Failed to fetch data. Please try again.",
          severity: "error",
        });
      }
    };

    fetchData();
  }, []);

  // Handle form field changes
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => {
      let updatedData = { ...prev, [name]: type === "checkbox" ? checked : value };

      if (name === "payment_terms") {
        updatedData.payment_terms_description =
          value.toLowerCase() === "credit" ? prev.payment_terms_description : "";
      }

      if (name === "delivery_date") {
        updatedData.delivery_date_hours = value === "Immediate" ? prev.delivery_date_hours : "";
        updatedData.delivery_date_days = value === "Within" ? prev.delivery_date_days : "";
      }

      return updatedData;
    });
  };

  // Handle product selection
  const handleProductSelection = (productId) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === productId
          ? { ...product, selected: !product.selected }
          : product
      )
    );

    setSelectedProducts((prevSelected) => {
      const product = products.find((p) => p.id === productId);
      if (!product) return prevSelected;

      if (product.selected) {
        return prevSelected.filter((p) => p.id !== productId);
      } else {
        return [
          ...prevSelected,
          {
            id: product.id,
            name: product.product_name,
            brand: product.brand,
            quantity: product.quantity,
            pieces: product.pieces,
          },
        ];
      }
    });
  };

  // Handle product field changes (quantity, pieces, brand)
  const handleProductFieldChange = (productId, field, value) => {
    const sanitizedValue =
      field === "quantity" || field === "pieces"
        ? value === "" || Number(value) >= 0
          ? value
          : ""
        : value;

    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === productId
          ? {
              ...product,
              quantity: field === "quantity" ? sanitizedValue : field === "pieces" ? "" : product.quantity,
              pieces: field === "pieces" ? sanitizedValue : field === "quantity" ? "" : product.pieces,
              brand: field === "brand" ? value : product.brand,
            }
          : product
      )
    );

    setSelectedProducts((prevSelected) =>
      prevSelected.map((selected) =>
        selected.id === productId
          ? {
              ...selected,
              quantity: field === "quantity" ? sanitizedValue : field === "pieces" ? "" : selected.quantity,
              pieces: field === "pieces" ? sanitizedValue : field === "quantity" ? "" : selected.pieces,
              brand: field === "brand" ? value : selected.brand,
            }
          : selected
      )
    );
  };

  // Validate form data
  const validateForm = () => {
    const errors = [];

    // Validate app_user_id
    if (!formData.app_user_id) {
      errors.push("Customer selection is required.");
    }

    // Validate selected products
    const selectedProductsList = products.filter((product) => product.selected);
    if (selectedProductsList.length === 0) {
      errors.push("At least one product must be selected.");
    } else {
      selectedProductsList.forEach((product) => {
        if (!product.brand) {
          errors.push(`Brand must be selected for product: ${product.product_name}.`);
        }
        if (!product.quantity && !product.pieces) {
          errors.push(
            `Either quantity or pieces must be provided for product: ${product.product_name}.`
          );
        } else if (product.quantity && product.pieces) {
          errors.push(
            `Only one of quantity or pieces can be provided for product: ${product.product_name}.`
          );
        } else {
          if (product.quantity && Number(product.quantity) < 0) {
            errors.push(
              `Quantity must be a non-negative number for product: ${product.product_name}.`
            );
          }
          if (product.pieces && Number(product.pieces) < 0) {
            errors.push(
              `Pieces must be a non-negative number for product: ${product.product_name}.`
            );
          }
        }
      });
    }

    // Validate payment terms
    if (!formData.payment_terms) {
      errors.push("Payment Terms is required.");
    }
    if (formData.payment_terms.toLowerCase() === "credit") {
      if (!formData.payment_terms_description) {
        errors.push("Number of Days is required when Payment Terms is Credit.");
      } else if (Number(formData.payment_terms_description) < 1) {
        errors.push("Number of Days must be a positive number.");
      }
    }

    // Validate delivery terms
    if (!formData.delivery_title) {
      errors.push("Delivery Terms is required.");
    }
    if (formData.delivery_title === "Delivered To") {
      if (!formData.delivery_address.trim()) {
        errors.push("Delivery Address is required when Delivery Terms is Delivered To.");
      }
    }

    // Validate delivery conditions
    if (!formData.delivery_conditions) {
      errors.push("Delivery Conditions is required.");
    }

    // Validate delivery date
    if (!formData.delivery_date) {
      errors.push("Delivery Date is required.");
    }
    if (formData.delivery_date === "Immediate") {
      if (!formData.delivery_date_hours) {
        errors.push("Hours is required for Delivery Date: Immediate.");
      } else if (Number(formData.delivery_date_hours) < 1) {
        errors.push("Hours must be a positive number.");
      }
    }
    if (formData.delivery_date === "Within") {
      if (!formData.delivery_date_days) {
        errors.push("Days is required for Delivery Date: Within.");
      } else if (Number(formData.delivery_date_days) < 1) {
        errors.push("Days must be a positive number.");
      }
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setSnackbar({
        open: true,
        message: validationErrors.join("\n"),
        severity: "error",
      });
      return;
    }

    const transformedData = {
      app_user_id: formData.app_user_id,
      payment_terms: formData.payment_terms,
      payment_terms_description:
        formData.payment_terms.toLowerCase() === "credit"
          ? formData.payment_terms_description
          : null,
      delivery_terms: formData.delivery_title,
      delivery_address: formData.delivery_address,
      delivery_condition: formData.delivery_conditions,
      delivery_date: formData.delivery_date,
      delivery_date_hours:
        formData.delivery_date === "Immediate" ? formData.delivery_date_hours : null,
      delivery_date_days:
        formData.delivery_date === "Within" ? formData.delivery_date_days : null,
      status: formData.status,
      products: selectedProducts.map((product) => ({
        product_id: product.id,
        brand_id: product.brand,
        quantity: product.quantity || null,
        pieces: product.pieces || null,
      })),
    };

    try {
      await axios.post(API_ENDPOINTS.CUSTOMER_ORDERS.CREATE, transformedData);

      setSnackbar({
        open: true,
        message: "Enquiry created successfully!",
        severity: "success",
      });

      // Reset form after successful submission
      setFormData({
        app_user_id: "",
        payment_terms: "",
        payment_terms_description: "",
        delivery_title: "",
        delivery_address: "",
        delivery_conditions: "",
        delivery_date: "",
        delivery_date_hours: "",
        delivery_date_days: "",
        status: "pending",
      });
      setProducts((prev) =>
        prev.map((p) => ({ ...p, selected: false, brand: "", quantity: "", pieces: "" }))
      );
      setSelectedProducts([]);

      setTimeout(() => {
        navigate("/customer-orders");
      }, 2000);
    } catch (error) {
      console.error("Error creating Enquiry :", error.response?.data || error.message);
      let errorMessage = "Error creating Enquiry : Something went wrong";
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .join("\n");
        errorMessage = `Validation errors:\n${errorMessages}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error creating Enquiry: ${error.response.data.message}`;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            {/* <Typography variant="h6" sx={{ mb: 2 }}>
              Customer Order Details
            </Typography> */}
            <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel id="app-user-label">Select Customer</InputLabel>
              <Select
                labelId="app-user-label"
             
                    id="app-user-label"
                    label="Select Customer"
                name="app_user_id"
                value={formData.app_user_id}
                onChange={handleChange}
              >
                <MenuItem value="" disabled>
                  Select Customer
                </MenuItem>
                {appUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                     {user.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mt={2}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add Products
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Select</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Pieces</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Checkbox
                            checked={product.selected}
                            onChange={() => handleProductSelection(product.id)}
                          />
                        </TableCell>
                        <TableCell>{product.product_name}</TableCell>
                        <TableCell>
                          <FormControl fullWidth>
                            <InputLabel id={`brand-label-${product.id}`}>Brand</InputLabel>
                            <Select
                              labelId={`brand-label-${product.id}`}
                              value={product.brand}
                              id={`brand-label-${product.id}`}
                              label="Brand"
                              onChange={(e) =>
                                handleProductFieldChange(product.id, "brand", e.target.value)
                              }
                              disabled={!product.selected}
                            >
                              <MenuItem value="" disabled>
                                Select Brand
                              </MenuItem>
                              {brands.map((brand) => (
                                <MenuItem key={brand.id} value={brand.id}>
                                  {brand.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={product.quantity}
                            onChange={(e) =>
                              handleProductFieldChange(product.id, "quantity", e.target.value)
                            }
                            disabled={!product.selected || product.pieces !== ""}
                            inputProps={{ min: 0 }}
                            label="Quantity"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={product.pieces}
                            onChange={(e) =>
                              handleProductFieldChange(product.id, "pieces", e.target.value)
                            }
                            disabled={!product.selected || product.quantity !== ""}
                            inputProps={{ min: 0 }}
                            label="Pieces"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box mt={2}>
              <Button type="submit" variant="contained">
                Create Enquiry
              </Button>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment Terms
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Payment Term</FormLabel>
              <RadioGroup
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
              >
                {paymentTerms.map((term) => (
                  <Box key={term.id}>
                    <FormControlLabel
                      value={term.name}
                      control={<Radio />}
                      label={term.name}
                    />
                    {term.name.toLowerCase() === "credit" &&
                      formData.payment_terms === term.name && (
                        <TextField
                          label="Number of Days"
                          name="payment_terms_description"
                          type="number"
                          value={formData.payment_terms_description}
                          onChange={handleChange}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                          inputProps={{ min: 1 }}
                        />
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Card>

          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Delivery Terms
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Delivery Term</FormLabel>
              <RadioGroup
                name="delivery_title"
                value={formData.delivery_title}
                onChange={handleChange}
              >
                {deliveryTerms.map((term) => (
                  <Box key={term.id}>
                    <FormControlLabel
                      value={term.name}
                      control={<Radio />}
                      label={term.name}
                    />
                    {term.name === "Delivered To" &&
                      formData.delivery_title === term.name && (
                        <TextField
                          label="Delivery Address"
                          name="delivery_address"
                          multiline
                          rows={3}
                          value={formData.delivery_address}
                          onChange={handleChange}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Card>

          <Card sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Delivery Conditions
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Delivery Condition</FormLabel>
              <RadioGroup
                name="delivery_conditions"
                value={formData.delivery_conditions}
                onChange={handleChange}
              >
                {deliveryConditionsOptions.map((condition) => (
                  <FormControlLabel
                    key={condition.id}
                    value={condition.name}
                    control={<Radio />}
                    label={condition.name}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Card>

          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Delivery Date
            </Typography>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Select Delivery Date</FormLabel>
              <RadioGroup
                name="delivery_date"
                value={formData.delivery_date}
                onChange={handleChange}
              >
                {deliveryDateOptions.map((option) => (
                  <Box key={option.id}>
                    <FormControlLabel
                      value={option.name}
                      control={<Radio />}
                      label={option.name}
                    />
                    {option.name === "Immediate" &&
                      formData.delivery_date === option.name && (
                        <TextField
                          label="Hours"
                          name="delivery_date_hours"
                          type="number"
                          value={formData.delivery_date_hours}
                          onChange={handleChange}
                          inputProps={{ min: 1 }}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                    {option.name === "Within" &&
                      formData.delivery_date === option.name && (
                        <TextField
                          label="Days"
                          name="delivery_date_days"
                          type="number"
                          value={formData.delivery_date_days}
                          onChange={handleChange}
                          inputProps={{ min: 1 }}
                          fullWidth
                          sx={{ mt: 1, ml: 4 }}
                          required
                        />
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Status
            </Typography>
            <FormControl fullWidth required>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="finalized">Finalized</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateCustomerOrderForm;